import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Edit, Image, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminKYCPage() {
  const { t } = useLanguage();
  const [kycList, setKycList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const [editFormData, setEditFormData] = useState({
    full_name: '',
    document_number: '',
    date_of_birth: '',
    address: '',
    occupation: '',
    purpose_of_transfer: '',
    residence_status: '',
    residence_card_number: '',
    residence_period: ''
  });

  useEffect(() => {
    fetchKYC();
  }, []);

  const fetchKYC = async () => {
    try {
      const response = await api.getAdminKYC();
      setKycList(response.data);
    } catch (error) {
      console.error('Failed to fetch KYC:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (kycId, action) => {
    try {
      await api.reviewKYC(kycId, action, reviewNotes);
      toast.success(t('admin_kyc_reviewed'));
      setDialogOpen(false);
      setSelectedKYC(null);
      setReviewNotes('');
      fetchKYC();
    } catch (error) {
      toast.error(t('admin_kyc_review_failed'));
    }
  };

  const openReviewDialog = (kyc) => {
    setSelectedKYC(kyc);
    setDialogOpen(true);
  };

  const openEditDialog = (kyc) => {
    setSelectedKYC(kyc);
    setEditFormData({
      full_name: kyc.full_name || '',
      document_number: kyc.document_number || '',
      date_of_birth: kyc.date_of_birth || '',
      address: kyc.address || '',
      occupation: kyc.occupation || '',
      purpose_of_transfer: kyc.purpose_of_transfer || '',
      residence_status: kyc.residence_status || '',
      residence_card_number: kyc.residence_card_number || '',
      residence_period: kyc.residence_period || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      await api.updateKYC(selectedKYC.id, editFormData);
      toast.success(t('admin_kyc_updated'));
      setEditDialogOpen(false);
      setSelectedKYC(null);
      fetchKYC();
    } catch (error) {
      toast.error(t('admin_kyc_update_failed'));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: t('submitted'), className: 'bg-blue-500/15 text-blue-700 border-blue-500/20' },
      approved: { label: t('approved'), className: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/20' },
      rejected: { label: t('rejected'), className: 'bg-red-500/15 text-red-700 border-red-500/20' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.className}`}>
        {config.label}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div data-testid="admin-kyc-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">
            { t('admin_kyc_review_title') }
          </h1>
          <p className="text-muted-foreground">
            {t('admin_kyc_review_title')}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
        ) : (
          <div className="space-y-4">
            {kycList.map((kyc) => (
              <div
                key={kyc.id}
                className="bg-card border border-border/50 rounded-2xl p-6"
                data-testid={`kyc-item-${kyc.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-primary mb-1">
                      {kyc.full_name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      提出日: {new Date(kyc.submitted_at).toLocaleString('ja-JP')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(kyc.status)}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(kyc)}
                      data-testid={`edit-kyc-${kyc.id}`}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      {t('edit')}
                    </Button>
                    {kyc.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => openReviewDialog(kyc)}
                        data-testid={`review-button-${kyc.id}`}
                      >
                        {t('admin_kyc_review_title')}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground mb-1">{t('kyc_doc_type_label')}</div>
                    <div className="font-medium">
                      {kyc.document_type === 'passport' ? t('kyc_doc_type_passport') :
                       kyc.document_type === 'residence_card' ? t('kyc_doc_type_residence') :
                       kyc.document_type === 'drivers_license' ? t('kyc_doc_type_license') :
                       kyc.document_type}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">{t('kyc_doc_number')}</div>
                    <div className="font-mono text-xs">{kyc.document_number}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">{t('kyc_dob')}</div>
                    <div>{kyc.date_of_birth}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-muted-foreground mb-1">{t('kyc_address')}</div>
                    <div>{kyc.address}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">{t('kyc_occupation')}</div>
                    <div>{kyc.occupation}</div>
                  </div>
                  <div className="col-span-3">
                    <div className="text-muted-foreground mb-1">{t('kyc_purpose')}</div>
                    <div>{kyc.purpose_of_transfer}</div>
                  </div>
                  {/* Residence Info */}
                  {kyc.residence_status && (
                    <div>
                      <div className="text-muted-foreground mb-1">{t('kyc_residence_status')}</div>
                      <div className="font-medium">{kyc.residence_status}</div>
                    </div>
                  )}
                  {kyc.residence_card_number && (
                    <div>
                      <div className="text-muted-foreground mb-1">{t('kyc_residence_card_number')}</div>
                      <div className="font-mono text-xs">{kyc.residence_card_number}</div>
                    </div>
                  )}
                  {kyc.residence_period && (
                    <div>
                      <div className="text-muted-foreground mb-1">{t('kyc_residence_period')}</div>
                      <div>{kyc.residence_period}</div>
                    </div>
                  )}
                </div>

                {/* Document Files */}
                {kyc.document_files && kyc.document_files.length > 0 && (
                  <div className="mt-4" data-testid={`kyc-documents-${kyc.id}`}>
                    <div className="text-sm text-muted-foreground mb-2 flex items-center gap-1.5">
                      <Image className="h-4 w-4" />
                      {t('kyc_submitted_docs')} ({kyc.document_files.length})
                    </div>
                    <div className="flex gap-3 flex-wrap">
                      {kyc.document_files.map((file, idx) => {
                        const isImage = file.content_type?.startsWith('image/') || file.file_url?.match(/\.(jpg|jpeg|png|webp)$/i);
                        const sideLabel = file.side === 'front' ? t('kyc_photo_front') :
                                          file.side === 'back' ? t('kyc_photo_back') :
                                          file.side === 'angled' ? t('kyc_photo_angled') : '';
                        return (
                          <div key={idx} className="relative group" data-testid={`kyc-doc-file-${kyc.id}-${idx}`}>
                            {isImage ? (
                              <img
                                src={`${API_URL}${file.file_url}`}
                                alt={sideLabel || file.filename || `${idx + 1}`}
                                className="h-24 w-24 rounded-lg object-cover border border-border cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => { setViewerImage(`${API_URL}${file.file_url}`); setImageViewerOpen(true); }}
                              />
                            ) : (
                              <a
                                href={`${API_URL}${file.file_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 h-24 px-4 rounded-lg border border-border bg-secondary/30 hover:bg-secondary/50 transition-colors"
                              >
                                <ExternalLink className="h-5 w-5 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground max-w-[80px] truncate">{file.filename || 'PDF'}</span>
                              </a>
                            )}
                            {sideLabel && (
                              <div className="text-xs text-center text-muted-foreground mt-1">{sideLabel}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {kyc.reviewer_notes && (
                  <div className="mt-4 p-4 bg-secondary/30 rounded-xl">
                    <div className="text-sm font-medium text-primary mb-1">{t('admin_kyc_notes')}</div>
                    <div className="text-sm text-muted-foreground">{kyc.reviewer_notes}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{ t('admin_kyc_review_title') }</DialogTitle>
              <DialogDescription>
                {selectedKYC?.full_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium mb-2 block">{t('admin_kyc_notes')}</label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={4}
                  placeholder={t('admin_kyc_notes_placeholder')}
                  data-testid="review-notes-input"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  className="flex-1"
                  onClick={() => handleReview(selectedKYC?.id, 'approve')}
                  data-testid="approve-button"
                >
                  {t('approved')}
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => handleReview(selectedKYC?.id, 'reject')}
                  data-testid="reject-button"
                >
                  {t('rejected')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit KYC Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('admin_kyc_review_title')}</DialogTitle>
              <DialogDescription>
                {selectedKYC?.full_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="space-y-2">
                <Label>{t('name')}</Label>
                <Input
                  value={editFormData.full_name}
                  onChange={(e) => setEditFormData({...editFormData, full_name: e.target.value})}
                  data-testid="edit-kyc-fullname"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('kyc_doc_number')}</Label>
                <Input
                  value={editFormData.document_number}
                  onChange={(e) => setEditFormData({...editFormData, document_number: e.target.value})}
                  data-testid="edit-kyc-document-number"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('kyc_dob')}</Label>
                <Input
                  value={editFormData.date_of_birth}
                  onChange={(e) => setEditFormData({...editFormData, date_of_birth: e.target.value})}
                  data-testid="edit-kyc-dob"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('kyc_address')}</Label>
                <Input
                  value={editFormData.address}
                  onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                  data-testid="edit-kyc-address"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('kyc_occupation')}</Label>
                <Input
                  value={editFormData.occupation}
                  onChange={(e) => setEditFormData({...editFormData, occupation: e.target.value})}
                  data-testid="edit-kyc-occupation"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('kyc_purpose')}</Label>
                <Input
                  value={editFormData.purpose_of_transfer}
                  onChange={(e) => setEditFormData({...editFormData, purpose_of_transfer: e.target.value})}
                  data-testid="edit-kyc-purpose"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('kyc_residence_status')}</Label>
                <Input
                  value={editFormData.residence_status}
                  onChange={(e) => setEditFormData({...editFormData, residence_status: e.target.value})}
                  data-testid="edit-kyc-residence-status"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('kyc_residence_card_number')}</Label>
                <Input
                  value={editFormData.residence_card_number}
                  onChange={(e) => setEditFormData({...editFormData, residence_card_number: e.target.value})}
                  data-testid="edit-kyc-residence-card"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('kyc_residence_period')}</Label>
                <Input
                  type="date"
                  value={editFormData.residence_period}
                  onChange={(e) => setEditFormData({...editFormData, residence_period: e.target.value})}
                  data-testid="edit-kyc-residence-period"
                />
              </div>
              <Button
                className="w-full"
                onClick={handleEditSubmit}
                data-testid="update-kyc-button"
              >
                {t('update')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        {/* Image Viewer Dialog */}
        <Dialog open={imageViewerOpen} onOpenChange={setImageViewerOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{t('kyc_documents')}</DialogTitle>
            </DialogHeader>
            {viewerImage && (
              <div className="flex justify-center mt-4">
                <img
                  src={viewerImage}
                  alt="Document"
                  className="max-h-[70vh] max-w-full rounded-lg object-contain"
                  data-testid="image-viewer-preview"
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
